import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import { SceneTemplate } from '@/model';
import { omitEmpty } from '@/util/typeUtil';
import { isValidParagraphData } from '@/util/helper/paragraphHelper';
import { refineSameNameFiles, replaceBlobUrlToFilenameAndGetNamedFiles } from '@/util/fileUtil';
import { useCreateTemplate, useTemplates, useUpdateTemplates, useDeleteTemplate } from '@/query/template';
import {
    useDialog, useModal, useConnectedUsers, useRouteHandler,
    useScenarioAutocompletes, useScenarioId, useUploadedFiles, useSyncForm
} from '@/hook';
import Button from '@/component/Button';
import { RoutingBlockDialog } from '@/component/modal';
import { SearchBar, SearchBarRef, TextInput } from '@/component/input';
import { errorToast, successToast } from '@/component/Toast';
import { ParagraphEditor } from '@/component/paragraphEditor';
import { AddIcon } from '@/assets/svgs/icons';

import TemplateList from './component/TemplateList';
import TemplateCreateModal, { TEMPLATE_CREATE_MODAL_ID } from './component/TemplateCrateModal';


const TemplateEditView: React.FC = () => {
    const intl = useIntl();
    const location = useLocation();
    const scenarioId = useScenarioId();
    const modal = useModal();
    const dialog = useDialog();
    const { isOtherConnected } = useConnectedUsers();
    const { removeStateKey } = useRouteHandler();
    const { setUserVariables, clearUserVariables } = useScenarioAutocompletes();
    const { syncForm } = useSyncForm();

    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [selectedTemplate, setSelectedTemplate] = useState<SceneTemplate>();
    const [modifiedTemplates, setModifiedTemplates] = useState<{ [key: number]: SceneTemplate }>({});
    const editorContainerRef = useRef<HTMLDivElement>(null);
    const searchBarRef = useRef<SearchBarRef>(null);
    const templateToSelectRef = useRef<SceneTemplate>();

    const { files, clear: clearFiles } = useUploadedFiles();
    const { data: allTemplates } = useTemplates(scenarioId);
    const { data: searchedTemplates } = useTemplates(scenarioId, searchKeyword);
    const { mutateAsync: createTemplate, isPending: isDuplicating } = useCreateTemplate(scenarioId);
    const { mutateAsync: updateTemplates, isPending: isUpdating } = useUpdateTemplates(scenarioId);
    const { mutateAsync: deleteTemplate, isPending: isDeleting } = useDeleteTemplate(scenarioId);

    const checkNameUsed = useCallback((name: string, id?: number) => {
        if (!name || !allTemplates?.length) {
            return false;
        }
        return allTemplates.some(template => (modifiedTemplates[template.id] ?? template)?.name?.trim() === name.trim() && template.id !== id);
    }, [allTemplates, modifiedTemplates]);

    const checkInvalidTemplate = useCallback((template: SceneTemplate) => {
        if (!template.name?.trim() || checkNameUsed(template.name, template.id)) {
            return true;
        }
        if (!template.paragraphs?.length || !template.paragraphs.every(isValidParagraphData)) {
            return true;
        }
        return false;
    }, [checkNameUsed]);

    const isSubmitting = isUpdating || isDeleting || isDuplicating;
    const invalidTemplateIds = useMemo(() => {
        return Object.values(modifiedTemplates).filter(checkInvalidTemplate).map(template => template.id);
    }, [modifiedTemplates, checkInvalidTemplate]);

    const getModifiedTemplate = (template?: SceneTemplate) => {
        if (!template) {
            return;
        }
        return modifiedTemplates[template.id] ?? template;
    };

    const submit = async () => {
        const refinedFiles = await refineSameNameFiles(files);
        const {
            data: templatesToSubmit,
            files: filesToSubmit
        } = replaceBlobUrlToFilenameAndGetNamedFiles(structuredClone(Object.values(modifiedTemplates)), refinedFiles);

        const formData = new FormData();
        Object.values(filesToSubmit).forEach((file) => {
            formData.append("files", file);
        });

        try {
            formData.append("templates", JSON.stringify(omitEmpty(templatesToSubmit) ?? {}));
        } catch (e) {
            console.error('Error getting form data from templates', e);
            throw e;
        }
        return updateTemplates(formData)
            .then(templates => {
                setSelectedTemplate(prev =>
                    structuredClone(templates.find(template => template.id === prev?.id) ?? prev
                ));
                setModifiedTemplates({});
                clearFiles();
                successToast(intl.formatMessage({ id: "t003" }));
                setTimeout(() => {
                    syncForm();
                }, 100);
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t004" }));
            });
    };

    const onClickSave = async () => {
        if (Object.keys(modifiedTemplates).length === 0 || invalidTemplateIds.length > 0) {
            return;
        }

        if (isOtherConnected) {
            dialog.open({
                variant: "primary",
                title: intl.formatMessage({ id: "i00033" }),
                content: intl.formatMessage({ id: "i00021" }),
                onConfirm: submit
            });
        } else {
            await submit();
        }
    };

    const debouncedChangeTemplate = useMemo(() => {
        const updateUserVariables = (allTemplates: SceneTemplate[], modifiedTemplates: { [key: number]: SceneTemplate }) => {
            const updatedTemplates = [...(allTemplates?.map(template => modifiedTemplates[template.id] ?? template) ?? [])];
            setUserVariables("template", updatedTemplates);
        };

        return _.debounce((id: number, key: keyof SceneTemplate, value: any) => {
            setModifiedTemplates(prev => {
                const prevTemplate = prev[id] ?? structuredClone(allTemplates?.find(template => template.id === id));
                const newModifiedTemplates = {
                    ...prev,
                    [id]: {
                        ...prevTemplate,
                        [key]: value
                    }
                };
                updateUserVariables(allTemplates!, newModifiedTemplates);
                return newModifiedTemplates;
            });
        }, 300);
    }, [allTemplates, setUserVariables]);

    const onCreate = (template: SceneTemplate) => {
        templateToSelectRef.current = template;
        searchBarRef.current?.clear();
    };

    const onDuplicate = async (templateId: number) => {
        const template = modifiedTemplates[templateId] ?? allTemplates?.find(t => t.id === templateId);
        if (!template) {
            return;
        }
        const form = {
            ...template,
            name: `${template.name} copy`,
            paragraphs: template.paragraphs?.map(paragraph => omitEmpty(structuredClone(paragraph)) ?? {}) ?? []
        };
        await createTemplate(form)
                .then(newTemplate => {
                    templateToSelectRef.current = newTemplate;
                    successToast(intl.formatMessage({ id: "t001" }));
                }).catch(error => {
                    console.error(error);
                    errorToast(intl.formatMessage({ id: "i03009" }));
                });
    };

    const onClickDelete = async (id: number) => {
        dialog.open({
            title: intl.formatMessage({ id: "i02012" }),
            content: intl.formatMessage({ id: "i02013" }),
            variant: "danger",
            onConfirm: async () => {
                let nextTemplate: SceneTemplate | undefined;
                if (id === selectedTemplate?.id && searchedTemplates && searchedTemplates.length > 1) {
                    const index = searchedTemplates.findIndex(template => template.id === id);
                    if (index !== -1) {
                        nextTemplate = index < searchedTemplates.length - 1 ? searchedTemplates[index + 1] : searchedTemplates[index - 1];
                    }
                }
                if (nextTemplate) {
                    templateToSelectRef.current = getModifiedTemplate(nextTemplate);
                }
                await deleteTemplate(id)
                    .then(() => {
                        successToast(intl.formatMessage({ id: "t005" }));
                    })
                    .catch(() => {
                        errorToast(intl.formatMessage({ id: "t006" }));
                    });
            }
        });
    };

    useEffect(() => {
        if (!searchedTemplates) {
            return;
        }
        if (templateToSelectRef.current && searchedTemplates.find(t => t.id === templateToSelectRef.current!.id)) {
            setSelectedTemplate(templateToSelectRef.current);
            templateToSelectRef.current = undefined;
            return;
        }
        if (location.state?.selectedTemplateId) {
            const target = searchedTemplates?.find(t => t.id === location.state.selectedTemplateId);
            setSelectedTemplate(getModifiedTemplate(target));
            removeStateKey('selectedTemplateId');
            return;
        }
        if (selectedTemplate && searchedTemplates.find(t => t.id === selectedTemplate.id)) {
            return;
        }
        setSelectedTemplate(getModifiedTemplate(searchedTemplates?.[0]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchedTemplates, location]);

    useEffect(() => {
        return () => {
            clearFiles();
            clearUserVariables();
        };
    }, [clearFiles, clearUserVariables]);

    return (<>
        <TemplateCreateModal checkNameUsedByOthers={checkNameUsed}
                             onCreate={onCreate}
        />
        <RoutingBlockDialog isBlocking={Object.values(modifiedTemplates).length > 0}
                            title={intl.formatMessage({ id: "i04041" })}
                            description={intl.formatMessage({ id: "i04042" })}
        />

        <main className="scenario-content-layout pr-3 flex flex-col">
            <div className="search-bar-container">
                <SearchBar ref={searchBarRef}
                           width={680}
                           placeholder={intl.formatMessage({ id: "i03004" })}
                           onSearch={setSearchKeyword}
                           formAriaLabel={intl.formatMessage({ id: "i03004" })}
                           clearButtonAriaLabel={intl.formatMessage({ id: "b0042" })}
                />
            </div>

            <div className="subtitle-container mt-5">
                <Button variant="text"
                        className="text-[13px]"
                        onClick={() => modal.open(TEMPLATE_CREATE_MODAL_ID)}
                >
                    <AddIcon className="size-3"/>
                    {intl.formatMessage({ id: "i03001" })}
                </Button>
            </div>

            <div className="h-full mt-3 min-h-0 w-full min-w-md-page flex flex-col gap-3">
                <div className="h-full min-h-0 flex gap-4">
                    <TemplateList templates={searchedTemplates ?? []}
                                  modifiedTemplates={modifiedTemplates}
                                  invalidTemplateIds={invalidTemplateIds}
                                  selectedId={selectedTemplate?.id}
                                  onClickItem={id => setSelectedTemplate(getModifiedTemplate(searchedTemplates?.find(t => t.id === id)))}
                                  onDuplicate={onDuplicate}
                                  onDelete={onClickDelete}
                                  isSubmitting={isSubmitting}
                    />

                    {selectedTemplate && (
                        <div key={selectedTemplate.id}
                             ref={editorContainerRef}
                             className="template-edit-content-container scroll-gap-container floating-menu-clipping-ancestors"
                             role="region"
                             aria-label={intl.formatMessage({ id: "i03002" })}
                        >
                            <div className="template-card">
                                <label htmlFor={`template-name-${selectedTemplate.id}`}
                                       className="field-label label-input-grid-label"
                                >
                                    {intl.formatMessage({ id: "i03002" })}
                                </label>
                                <div className="flex flex-col gap-1">
                                    <TextInput id={`template-name-${selectedTemplate.id}`}
                                               required
                                               useDirty
                                               defaultValue={selectedTemplate.name}
                                               invalid={checkNameUsed(modifiedTemplates[selectedTemplate.id]?.name, selectedTemplate.id)}
                                               onChange={e => debouncedChangeTemplate(selectedTemplate.id, "name", e.target.value)}
                                    />
                                    {checkNameUsed(modifiedTemplates[selectedTemplate.id]?.name, selectedTemplate.id) && (
                                        <div className="col-span-2 font-[400] text-[12px] leading-[1.5] text-point-red">
                                            {intl.formatMessage({ id: "i03007" })}
                                        </div>
                                    )}
                                </div>
                            </div>
                
                            <ParagraphEditor id={selectedTemplate.id}
                                             className="ml-0.5"
                                             defaultParagraphs={selectedTemplate.paragraphs}
                                             onChange={paragraphs => debouncedChangeTemplate(selectedTemplate.id, "paragraphs", paragraphs)}
                                             scrollToBottom={() => {
                                                setTimeout(() => {
                                                    editorContainerRef.current?.scrollTo({
                                                        top: editorContainerRef.current?.scrollHeight,
                                                    });
                                                }, 100);
                                             }}
                            />
                        </div>
                    )}

                    {searchedTemplates?.length === 0 && (
                        <div className="template-empty-notice-container">
                            <div className="text-[14px] font-[400] text-black-400">
                                {searchKeyword
                                    ? intl.formatMessage({ id: "b0010" })
                                    : intl.formatMessage({ id: "i03008" })
                                }
                            </div>
                        </div>
                    )}
                </div>

                {(!!searchedTemplates?.length || selectedTemplate) && (
                    <div className="scroll-gap-container-align mt-auto flex justify-end items-center gap-2">
                        <Button size="xs"
                                variant="danger"
                                onClick={() => selectedTemplate && onClickDelete(selectedTemplate.id)}
                                disabled={isSubmitting}
                        >
                            {intl.formatMessage({ id: "b0006" })}
                        </Button>
        
                        <Button size="xs"
                                variant="primary"
                                onClick={onClickSave}
                                disabled={isSubmitting || Object.keys(modifiedTemplates).length === 0 || invalidTemplateIds.length > 0}
                        >
                            {intl.formatMessage({ id: "b0007" })}
                        </Button>
                    </div>
                )}
            </div>
        </main>
    </>);
};

export default TemplateEditView;
